import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config/api';
import './VenueBusinessIntelligence.css';

const VenueBusinessIntelligence = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isRestaurant = location.pathname.includes('/restaurant/');
  const apiBase = isRestaurant ? 'restaurant-owner' : 'store-owner';

  const [filter, setFilter] = useState('last30days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [apiBase]);

  useEffect(() => {
    fetchData();
  }, [filter, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        filter: filter !== 'custom' ? filter : undefined,
        startDate: filter === 'custom' ? startDate : undefined,
        endDate: filter === 'custom' ? endDate : undefined
      };
      const response = await axios.get(`${API_URL}/${apiBase}/business-intelligence`, { params });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching business intelligence:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    if (newFilter !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const formatCurrency = (amount) => `€${(amount || 0).toFixed(2)}`;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const customerName = (customer) => {
    if (!customer) return '—';
    const parts = [customer.firstName, customer.lastName].filter(Boolean);
    return parts.length ? parts.join(' ') : customer.email || '—';
  };

  return (
    <div className="venue-business-intelligence">
      <div className="bi-header">
        <h2>{t('admin.businessIntelligence')}</h2>
      </div>

      <div className="bi-filters">
        <div className="filter-group">
          <label>{t('admin.timePeriod')}</label>
          <div className="time-filters">
            <button
              className={filter === 'today' ? 'active' : ''}
              onClick={() => handleFilterChange('today')}
            >
              {t('admin.today')}
            </button>
            <button
              className={filter === 'yesterday' ? 'active' : ''}
              onClick={() => handleFilterChange('yesterday')}
            >
              {t('admin.yesterday')}
            </button>
            <button
              className={filter === 'last7days' ? 'active' : ''}
              onClick={() => handleFilterChange('last7days')}
            >
              {t('admin.last7Days')}
            </button>
            <button
              className={filter === 'last30days' ? 'active' : ''}
              onClick={() => handleFilterChange('last30days')}
            >
              {t('admin.last30Days')}
            </button>
            <button
              className={filter === 'custom' ? 'active' : ''}
              onClick={() => handleFilterChange('custom')}
            >
              {t('admin.customRange')}
            </button>
          </div>
        </div>
        {filter === 'custom' && (
          <div className="filter-group">
            <label>{t('admin.dateRange')}</label>
            <div className="date-range">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span>{t('admin.to')}</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
              />
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-spinner">{t('admin.loadingMetrics')}</div>
      )}

      {!loading && data && (
        <>
          <div className="venue-metrics-section">
            <h3>{isRestaurant ? t('admin.yourRestaurantSales') : t('admin.yourStoreSales')}</h3>
            <div className="business-metrics-grid venue-metrics">
              <div className="metric-card">
                <h4>{t('admin.dateRange')}</h4>
                <p className="metric-value small">
                  {formatDate(data.dateRange.start)} – {formatDate(data.dateRange.end)}
                </p>
              </div>
              <div className="metric-card highlight">
                <h4>{t('admin.totalOrders')}</h4>
                <p className="metric-value">{data.totalOrders}</p>
              </div>
              <div className="metric-card highlight">
                <h4>{t('admin.salesTotalAmount')}</h4>
                <p className="metric-value">{formatCurrency(data.salesTotalAmount)}</p>
              </div>
            </div>
          </div>

          <div className="recent-orders-section">
            <h3>{t('admin.recentCompletedOrders')}</h3>
            {data.recentCompletedOrders && data.recentCompletedOrders.length > 0 ? (
              <div className="table-container">
                <table className="venue-bi-table">
                  <thead>
                    <tr>
                      <th>{t('admin.orderNumber')}</th>
                      <th>{t('admin.customer')}</th>
                      <th>{t('admin.total')}</th>
                      <th>{t('admin.date')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentCompletedOrders.map((order) => (
                      <tr key={order._id}>
                        <td>{order.orderNumber || order._id?.slice(-6)}</td>
                        <td>
                          {customerName(order.customer)}
                          {order.customer?.email && (
                            <small className="customer-email">{order.customer.email}</small>
                          )}
                        </td>
                        <td>{formatCurrency(order.total)}</td>
                        <td>{formatDateTime(order.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">{t('admin.noCompletedOrders')}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VenueBusinessIntelligence;
